import { VaultAllocation } from '@components/vault/VaultAllocation'
import VaultExposure from '@components/vault/VaultExposure'
import { VaultFormBox } from '@components/vault/VaultFormBox'
import { VaultStats } from '@components/vault/VaultStats'
import useSettingsStore from '@store/settingsStore'
import SEO from 'components/Common/SEO'
import { Mode } from 'components/Synthetics/GmSwap/GmSwapBox/GmSwapBox'
import { getSyntheticsDepositMarketKey } from 'config/localStorage'
import { useMarketsInfo } from 'domain/synthetics/markets'
import { useChainId } from 'rfx/lib/chains'
import { getPageTitle } from 'rfx/lib/legacy'
import { useLocalStorageSerializeKey } from 'rfx/lib/localStorage'
import { usePendingTxns } from 'rfx/lib/usePendingTxns'
import useBreakpoints from 'hooks/useBreakpoints'
import React, { useEffect, useState } from 'react'
import { cn } from 'utils/classnames'
import SharePriceChart from './SharePriceChart'
import commonPoolStore from '@store/commonPoolStore'
import { commonPoolContracts } from 'config/contracts'
import useSWR from 'swr'
import CommonPool from 'abis/CommonPool.json'
import { getProvider } from 'rfx/lib/rpc'
import { formatPrice } from 'apis/birdeye/helpers'
import { useCommonPoolAPR } from 'hooks/vault/useCommonPoolAPR'
import dayjs from 'dayjs'
import { formatTradeActionTimestampISO } from 'components/Synthetics/TradeHistoryRow/utils/shared'
import { VaultInfoCard } from './VaultInfoCard'
// import SonicBanner from './SonicBanner'
import Accordian from '@components/Accordian'
import Button from '@components/shared/Button'
import MobileModal from '@components/shared/MobileModal'
import { Contract } from 'utils/ethersImport'

enum Operation {
  Deposit = 'Deposit',
  Withdrawal = 'Withdrawal',
  Bridge = 'Bridge',
}

const VaultHomePage = () => {
  const isPro = useSettingsStore((state) => state.isPro())
  const { below } = useBreakpoints()
  const { chainId } = useChainId()
  const [showMobileModal, setShowMobileModal] = useState(false)
  const provider = getProvider(undefined, chainId)
  const setSelectedVault = commonPoolStore((store) => store.setSelectedVault)

  const selectedVault = commonPoolStore((store) => store.selectedVault)
  const commonPoolAddress = selectedVault.value
  const commonPoolAsset = selectedVault.label

  const contract = new Contract(commonPoolAddress, CommonPool.abi, provider)
  const { marketsInfoData = {}, tokensData } = useMarketsInfo()
  const markets = Object.values(marketsInfoData)

  const [, setPendingTxns] = usePendingTxns()

  const [operation, setOperation] = useState<Operation>(Operation.Deposit)
  let [mode, setMode] = useState<Mode>(Mode.Single)

  const { lastRebalance } = useCommonPoolAPR()

  const { data, isLoading: loading } = useSWR(
    `vault-home-page-${selectedVault.value}-${chainId}`,
    {
      fetcher: async () => {
        try {
          const totalAssets = await contract.totalAssets()
          const totalSupply = await contract.totalSupply()
          const symbol = await contract.symbol()
          const name = await contract.name()
          const decimals = await contract.decimals()

          const convertToAssets = await contract.convertToAssets(
            Math.pow(10, 18).toString(), // decimals for share to asset price
          )
          return {
            totalAssets,
            totalSupply,
            convertToAssets,
            symbol,
            name,
            decimals,
          }
        } catch (e) {
          return {
            totalAssets: 0,
            totalSupply: 0,
            convertToAssets: 0,
            symbol: '',
            name: '',
            decimals: 18,
          }
        }
      },
    },
  )

  useEffect(() => {
    if (operation === Operation.Withdrawal) {
      setMode(Mode.Pair)
    }
  }, [operation])

  const [selectedMarketKey, setSelectedMarketKey] = useLocalStorageSerializeKey<
    string | undefined
  >(getSyntheticsDepositMarketKey(chainId), undefined)

  useEffect(() => {
    setSelectedVault({
      label: commonPoolContracts[chainId][0].label as string,
      value: commonPoolContracts[chainId][0].value as string,
    })
  }, [chainId, setSelectedVault])

  const header = () => {
    return (
      <div className="flex flex-col justify-between gap-4 md:gap-4">
        <div className="hidden justify-start text-base font-bold leading-7 text-th-fgd-1 lg:block">
          Shared Liquidity Vault (SLV) Overview
        </div>

        <div className="flex flex-col gap-6 lg:flex-row lg:gap-10 lg:pr-4 2xl:gap-16">
          {commonPoolContracts[chainId].map((item) => {
            return (
              <VaultInfoCard key={item.label} info={item} chainId={chainId} />
            )
          })}
        </div>
      </div>
    )
  }

  const section3 = () => {
    return (
      <div className="mt-4 flex w-full flex-col gap-4 lg:mt-4">
        <div className="hidden justify-start border-b border-t border-th-input-border py-2 text-base font-bold leading-7 text-th-fgd-1 lg:block lg:border-none lg:py-0 lg:text-base">
          Vault Info
        </div>
        <div className="flex-1 text-xs font-medium leading-tight text-th-fgd-1 lg:pb-2 lg:text-sm fhd:text-sm">
          {`Deposit ${commonPoolAsset} anytime to receive SLV tokens based on current vault
        TVL.  Withdrawals are queued up and claimable in the next epoch.`}
        </div>
        <div className="grid flex-1 grid-cols-2 items-center justify-start gap-y-4 lg:flex lg:gap-10 lg:gap-y-0">
          <VaultInfoSection
            label={`Total ${commonPoolAsset} Amount of assets`}
            value={
              loading
                ? '...'
                : formatPrice(
                    Number(data?.totalAssets) * Math.pow(10, -data?.decimals),
                    2,
                  )
            }
            subValue={commonPoolAsset}
          />

          <VaultInfoSection
            className="items-end justify-end text-right"
            label={`Total Supply of SLV ${commonPoolAsset} tokens`}
            value={
              loading
                ? '...'
                : formatPrice(
                    Number(data?.totalSupply * Math.pow(10, -data?.decimals)),
                    2,
                  )
            }
            subValue={'SLV ' + commonPoolAsset}
          />

          <VaultInfoSection
            label="Last Rebalance"
            value={
              lastRebalance
                ? dayjs(formatTradeActionTimestampISO(lastRebalance)).format(
                    'DD MMM YYYY',
                  )
                : '-'
            }
            subValue={
              lastRebalance
                ? dayjs(formatTradeActionTimestampISO(lastRebalance)).format(
                    ' hh:mm a',
                  )
                : '-'
            }
          />
        </div>
      </div>
    )
  }
  if (below.xl) {
    return (
      <SEO title={getPageTitle('Vault')}>
        <div>
          {/* <SonicBanner /> */}
          <div
            className="m-4 overflow-auto pb-24"
            style={{ height: '-webkit-fill-available' }}
          >
            <Accordian titleContent="Vault Overview" defaultOpen={true}>
              {header()}
            </Accordian>
            <Accordian titleContent="Vault Info"> {section3()}</Accordian>
            <Accordian titleContent="Vault Exposure">
              {' '}
              <div>
                <div className="pt-0 text-xs font-medium text-th-fgd-1 lg:pt-2 fhd:text-sm">
                  <span className="text-th-active">SLV</span> automatically
                  rebalances liquidity to the best performing markets with{' '}
                  <span className="text-th-active">0 impermanent loss</span>.
                </div>
                <VaultAllocation />
              </div>
            </Accordian>
            <Accordian titleContent="Share Price Chart">
              <div className="h-[250px]">
                <SharePriceChart />
              </div>
            </Accordian>
          </div>

          <div className="fixed bottom-14 w-full px-4">
            <Button
              className="h-12 w-full !bg-th-active font-body text-base"
              onClick={() => setShowMobileModal(true)}
            >
              ADD LIQUIDITY
            </Button>
          </div>
          <MobileModal
            isOpen={showMobileModal}
            onClose={() => setShowMobileModal(false)}
            hasTitle={true}
            hideClose={false}
            title=""
            hideTitleBorder={true}
          >
            <div>
              <VaultFormBox
                selectedMarketAddress={selectedMarketKey}
                markets={markets}
                marketsInfoData={marketsInfoData}
                tokensData={tokensData}
                onSelectMarket={setSelectedMarketKey}
                setPendingTxns={setPendingTxns}
                operation={operation}
                mode={mode}
                setMode={setMode}
                setOperation={setOperation}
              />
              <VaultStats />
              <VaultExposure />
            </div>
          </MobileModal>
        </div>
      </SEO>
    )
  }

  return (
    <SEO title={getPageTitle('Vault')}>
      <div className="flex h-full w-full justify-between">
        <div className="flex w-full flex-col overflow-auto">
          {/* <SonicBanner /> */}

          <div className="mx-6 my-6">
            {header()}

            {/* section 3 */}
            <div className="h-auto">
              {section3()}

              <div className="mt-4">
                <div className="flex !h-[390px] w-full gap-4 lg:gap-6 2xl:gap-6">
                  <div className="h-full flex-1 lg:rounded-sm lg:border lg:border-patek-neutral-celestial-grey-800 lg:bg-patek-neutral-celestial-grey-1000 lg:p-4">
                    <div className="justify-start text-2xl font-bold leading-7 text-th-fgd-1">
                      Vault Exposure
                    </div>

                    <VaultAllocation />
                  </div>
                  <div className="flex-1 lg:rounded-sm lg:border lg:border-patek-neutral-celestial-grey-800 lg:bg-patek-neutral-celestial-grey-1000 lg:p-4">
                    <div className="h-[350px] w-full">
                      <SharePriceChart />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={cn(
            'relative flex shrink-0 flex-col overflow-scroll border-l border-th-input-border',
            isPro ? 'w-[316px]' : 'w-[396px]',
          )}
        >
          <div className="px-4">
            <VaultFormBox
              selectedMarketAddress={selectedMarketKey}
              markets={markets}
              marketsInfoData={marketsInfoData}
              tokensData={tokensData}
              onSelectMarket={setSelectedMarketKey}
              setPendingTxns={setPendingTxns}
              operation={operation}
              mode={mode}
              setMode={setMode}
              setOperation={setOperation}
            />
            <VaultStats />
            <VaultExposure />
          </div>
        </div>
      </div>
    </SEO>
  )
}

export default VaultHomePage

const VaultInfoSection = ({
  label,
  value,
  subValue,
  className,
}: {
  label: string
  value: string
  subValue: string
  className?: string
}) => {
  return (
    <div
      className={cn(
        'flex flex-col items-start justify-start align-middle lg:flex-1 lg:items-start lg:justify-center lg:gap-3 lg:rounded-sm lg:border lg:border-patek-neutral-celestial-grey-800 lg:bg-patek-neutral-celestial-grey-1000 lg:p-4',
        className,
      )}
    >
      <div className="justify-start text-xs font-normal capitalize leading-tight text-patek-neutral-celestial-grey-500 lg:font-medium fhd:text-sm">
        {label}
      </div>
      <div className="flex flex-col items-start justify-start gap-1 lg:flex-row lg:items-end lg:justify-end">
        <div className="justify-start text-xs font-semibold leading-normal text-th-fgd-1 lg:text-sm lg:font-medium fhd:text-sm">
          {value}
        </div>
        <div className="justify-start text-xs font-medium leading-normal text-patek-neutral-celestial-grey-500 lg:text-sm fhd:text-sm">
          {subValue}
        </div>
      </div>
    </div>
  )
}
